import { Route } from '@angular/router';
import { AuthGuard } from 'shared/guards/auth.guard';
import { IsAuthenticatedAdminGuard } from 'shared/guards/is-authenticated-admin.guard';
import { IsNotAuthenticatedGuard } from 'shared/guards/is-not-authenticated.guard';
import { KsefEnabledGuard } from 'shared/guards/ksef-enabled.guard';
import { RestApiEnabledGuard } from 'shared/guards/rest-api-enabled.guard';
import { SubscriptionActiveGuard } from 'shared/guards/subscription-active.guard';
import { LayoutComponent } from 'shared/layout/layout.component';
import { SEOData } from 'shared/seo-data';
import { AccountComponent } from './account/account.component';
import { accountResolver } from './account/account.resolver';
import { ClientsComponent } from './account/clients/clients.component';
import { DashboardComponent } from './account/dashboard/dashboard.component';
import { DocumentsComponent } from './account/documents/documents.component';
import { IssueComponent } from './account/documents/issue/issue.component';
import { PreviewComponent } from './account/documents/preview/preview.component';
import { CreateProductComponent } from './account/products/create-product/create-product.component';
import { ProductsComponent } from './account/products/products.component';
import { AdminComponent } from './admin/admin.component';
import { AnnouncementsComponent } from './admin/announcements/announcements.component';
import { CreateOrUpdateAnnouncementComponent } from './admin/announcements/create-or-update-announcement/create-or-update-announcement.component';
import { BackupComponent } from './admin/backup/backup.component';
import { AdminDashboardComponent } from './admin/dashboard/dashboard.component';
import { KsefPanelComponent } from './admin/ksef-panel/ksef-panel.component';
import { UserComponent } from './admin/users/user/user.component';
import { UsersComponent } from './admin/users/users.component';
import { SubscriptionsComponent } from './admin/subscriptions/subscriptions.component';
import { CampaignsComponent } from './admin/campaigns/campaigns.component';
import { CampaignEditorComponent } from './admin/campaigns/campaign-editor/campaign-editor.component';
import { SendNotificationComponent } from './admin/notifications/send-notification.component';
import { StatisticsComponent } from './admin/statistics/statistics.component';
import { AdminRestApiComponent } from './admin/rest-api/admin-rest-api.component';
import { AuthComponent } from './auth/auth.component';
import { ForgotPasswordPageComponent } from './auth/forgot-password-page/forgot-password-page.component';
import { GoogleCallbackComponent } from './auth/google-callback/google-callback.component';
import { RegisterPageComponent } from './auth/register-page/register.page.component';
import { RemoteLoginComponent } from './tools/remote-login/remote-login.component';
import { DocumentsExpensesComponent } from './account/documents-expenses/documents-expenses.component';
import { KsefComponent } from './account/ksef/ksef.component';
import { IntegrationsComponent } from './account/integrations/integrations.component';
import { SubscriptionsListComponent } from './account/subscriptions/subscriptions.component';

export const appRoutes: Route[] = [
  {
    path: '',
    component: LayoutComponent,
    data: { layout: 'empty' },
    canActivateChild: [IsNotAuthenticatedGuard],
    children: [
      { path: '', redirectTo: 'logowanie', pathMatch: 'full' },
      {
        path: 'logowanie',
        component: AuthComponent,
        data: { seo: SEOData.LOGIN }
      },
      {
        path: 'rejestracja',
        component: RegisterPageComponent,
        data: { seo: SEOData.REGISTER }
      },
      {
        path: 'przypomnij-haslo',
        component: ForgotPasswordPageComponent,
        data: { seo: SEOData.FORGOT_PASSWORD }
      },
      {
        path: 'przypomnij-haslo/:token/:emailAddress',
        component: ForgotPasswordPageComponent,
        data: { seo: SEOData.FORGOT_PASSWORD }
      },
      {
        path: 'remote-login/:oneTimeToken',
        component: RemoteLoginComponent,
      },
      {
        path: 'auth/google/callback',
        component: GoogleCallbackComponent,
      }
    ]
  },
  {
    path: '',
    component: LayoutComponent,
    data: { layout: 'modern' },
    children: [
      {
        path: 'konto',
        component: AccountComponent,
        resolve: { initialData: accountResolver },
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        children: [
          { path: '', redirectTo: 'dokumenty', pathMatch: 'full' },
          { path: 'pulpit', component: DashboardComponent, data: { seo: { title: "Pulpit" } } },

          // Dokumenty - wystawianie wymaga aktywnej subskrypcji
          { path: 'dokumenty', component: DocumentsComponent, data: { seo: { title: "Dokumenty" } } },
          { path: 'dokumenty/:id', component: IssueComponent, canActivate: [SubscriptionActiveGuard], data: { seo: SEOData.ISSUE, footer: false } },
          { path: 'dokumenty/:id/podglad', component: PreviewComponent, data: { seo: SEOData.ISSUE, footer: false } },

          // { path: 'koszty', component: DocumentsExpensesComponent, data: { seo: { title: "Koszty"}} },
          { path: 'ksef', component: KsefComponent, canMatch: [KsefEnabledGuard], data: { seo: { title: "KSeF" } } },
          // { path: 'ksef/faktury', canMatch: [KsefEnabledGuard], loadComponent: () => import('./account/ksef/ksef-invoices-list/ksef-invoices-list.component').then(m => m.KsefInvoicesListComponent), data: { seo: { title: "Faktury w KSeF"}} },
          { path: 'integracje', component: IntegrationsComponent, canMatch: [RestApiEnabledGuard], data: { seo: { title: "Integracje" } } },
          {
            path: 'integracje/rest-api', canMatch: [RestApiEnabledGuard], loadComponent: () => import('./account/integrations/rest-api/rest-api.component').then(m => m.RestApiComponent), data: { seo: { title: "REST API" } }, children: [
              { path: 'konta', loadComponent: () => import('./account/integrations/rest-api/api-accounts/api-accounts.component').then(m => m.ApiAccountsComponent) },
              { path: 'webhooki', loadComponent: () => import('./account/integrations/rest-api/webhooks/webhooks.component').then(m => m.WebhooksComponent) },
              { path: 'logi', loadComponent: () => import('./account/integrations/rest-api/api-logs/api-logs.component').then(m => m.ApiLogsComponent) },
              { path: '', redirectTo: 'konta', pathMatch: 'full' }
            ]
          },

          // Produkty
          { path: 'produkty', component: ProductsComponent, data: { seo: { title: "Produkty" } } },
          { path: 'produkty/utworz', component: CreateProductComponent, canActivate: [SubscriptionActiveGuard], data: { seo: { title: "Dodaj produkt" } } },
          { path: 'produkty/:id', loadChildren: () => import('./account/products/product-preview/product-preview.routes'), data: { seo: { title: "Produkty" } } },

          // Klienci
          { path: 'klienci', component: ClientsComponent, data: { seo: { title: "Klienci" } } },
          { path: 'klienci/:id', loadChildren: () => import('./account/clients/client-preview/client-preview.routes'), data: { seo: { title: "Klienci" } } },
          // { path: 'test', component: TestingComponent, data: { seo: {title: "Test" }} },
          { path: 'ustawienia', loadChildren: () => import('./account/settings/settings.routes'), data: { seo: { title: "Ustawienia" } } },
          { path: 'moje-konto', loadChildren: () => import('./account/my-account/my-account.routes'), data: { seo: { title: "Moje konto" } } },
          { path: 'subskrypcje', component: SubscriptionsListComponent, data: { seo: { title: "Subskrypcje" } } },

          // Dev sandbox — podgląd komponentów (tylko do iteracji UI)
          { path: 'dev/snackbars', loadComponent: () => import('./account/dev/snackbar-preview/snackbar-preview.component').then(m => m.SnackbarPreviewComponent), data: { seo: { title: "Podgląd snackbarów" } } }
        ]
      },
      {
        path: 'admin',
        component: AdminComponent,
        canActivate: [IsAuthenticatedAdminGuard],
        canActivateChild: [IsAuthenticatedAdminGuard],
        children: [
          { path: '', redirectTo: 'pulpit', pathMatch: 'full' },
          { path: 'pulpit', component: AdminDashboardComponent, data: { title: "Pulpit" } },
          { path: 'users', component: UsersComponent, data: { title: "Użytkownicy" } },
          {
            path: 'users/:id',
            component: UserComponent,
            loadChildren: () => import('./admin/users/user/user-tabs.routes').then(m => m.userTabRoutes)
          },
          { path: 'announcements', component: AnnouncementsComponent, data: { title: "Ogłoszenia" } },
          { path: 'announcements/:id', component: CreateOrUpdateAnnouncementComponent, data: { title: "Ogłoszenie" } },
          { path: 'backup', component: BackupComponent, data: { title: "Backup" } },
          { path: 'ksef', component: KsefPanelComponent, data: { title: "Monitoring KSeF" } },
          { path: 'rest-api', component: AdminRestApiComponent, data: { title: "REST API" } },
          { path: 'subscriptions', component: SubscriptionsComponent, data: { title: "Subskrypcje" } },
          { path: 'campaigns', component: CampaignsComponent, data: { title: "Kampanie" } },
          { path: 'campaigns/:id', component: CampaignEditorComponent, data: { title: "Edytor kampanii" } },
          { path: 'notifications', component: SendNotificationComponent, data: { title: "Powiadomienia" } },
          { path: 'statistics', component: StatisticsComponent, data: { title: "Statystyki" } },
        ]
      },
    ]
  },
  {
    path: '**',
    redirectTo: '/logowanie',
    pathMatch: 'full'
  }
]